export const OPPORTUNITY_TYPE_KEYS = [
  "ADMISSION",
  "LAB",
  "RADIOLOGY",
  // "DIAGNOSTICS",
  "DR_CONSULTATION",
  "FOLLOWUP",
  "PHARMACY",
  "PACKAGE",
  "PHYSIOTHERAPY",
] as const;

export type OpportunityTypeKey = (typeof OPPORTUNITY_TYPE_KEYS)[number];

export const OPPORTUNITY_TYPE_LABEL_BY_KEY: Record<OpportunityTypeKey, string> =
  {
    ADMISSION: "Admission",
    LAB: "Lab",
    RADIOLOGY: "Radiology",
    // DIAGNOSTICS: "Diagnostics",
    DR_CONSULTATION: "Dr Consultation",
    FOLLOWUP: "Follow Up",
    PHARMACY: "Pharmacy",
    PACKAGE: "Package",
    PHYSIOTHERAPY: "Physiotherapy",
  };

export const OPPORTUNITY_TYPE_OPTIONS = OPPORTUNITY_TYPE_KEYS.map((key) => ({
  id: key,
  label: OPPORTUNITY_TYPE_LABEL_BY_KEY[key],
}));

const OPPORTUNITY_TYPE_ALIAS_TO_KEY: Record<string, OpportunityTypeKey> = {
  ADMISSION: "ADMISSION",
  LAB: "LAB",
  RADIOLOGY: "RADIOLOGY",
  // DIAGNOSTICS: "DIAGNOSTICS",
  // DIAGNOSTIC: "DIAGNOSTICS",
  PROCEDURE: "DR_CONSULTATION",
  DR_CONSULTATION: "DR_CONSULTATION",
  DR_CONSULT: "DR_CONSULTATION",
  CONSULTATION: "DR_CONSULTATION",
  DR_ADMISSION: "DR_CONSULTATION",
  FOLLOWUP: "FOLLOWUP",
  FOLLOW_UP: "FOLLOWUP",
  FOLLOWUPS: "FOLLOWUP",
  FOLLOW_UPS: "FOLLOWUP",
  PHARMACY: "PHARMACY",
  PACKAGE: "PACKAGE",
  PHYSIOTHERAPY: "PHYSIOTHERAPY",
  PHYSIO: "PHYSIOTHERAPY",
};

function normalizeOpportunityTypeAliasKey(input: string): string {
  return input
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function normalizeOpportunityType(
  input: string
): OpportunityTypeKey | null {
  const normalized = normalizeOpportunityTypeAliasKey(input);
  return OPPORTUNITY_TYPE_ALIAS_TO_KEY[normalized] ?? null;
}

/**
 * Opportunity types that support Book Dr. Consultation (details panel CTA +
 * Book Appointment opportunity search: OPEN + these types).
 */
export const OPPORTUNITY_TYPES_FOR_BOOK_DR_CONSULTATION = [
  "DR_CONSULTATION",
  "FOLLOWUP",
  "REFERRAL",
] as const;

/** Comma-separated type query for GET /opportunities/patient/:id/list */
export const BOOK_DR_CONSULTATION_OPPORTUNITY_TYPES_QUERY =
  OPPORTUNITY_TYPES_FOR_BOOK_DR_CONSULTATION.join(",");

const BOOK_DR_CONSULTATION_TYPES_SET = new Set<string>(
  OPPORTUNITY_TYPES_FOR_BOOK_DR_CONSULTATION
);

export function opportunityTypeSupportsBookDrConsultation(
  type: string
): boolean {
  if (!type.trim()) return false;
  const normalized = normalizeOpportunityTypeAliasKey(type);
  return BOOK_DR_CONSULTATION_TYPES_SET.has(normalized);
}

/** Opportunity types that expose Service Details (admission, lab, radiology, package, pharmacy). */
export const OPPORTUNITY_TYPES_WITH_SERVICE_DETAILS = [
  "ADMISSION",
  "LAB",
  "RADIOLOGY",
  "PHARMACY",
  "PACKAGE",
] as const satisfies readonly OpportunityTypeKey[];

const OPPORTUNITY_TYPES_WITH_SERVICE_DETAILS_SET = new Set<string>(
  OPPORTUNITY_TYPES_WITH_SERVICE_DETAILS
);

/** Raw API type strings outside OPPORTUNITY_TYPE_KEYS that still use service details (e.g. LAB_TEST → lab). */
const SERVICE_DETAILS_EXTRA_TYPE_ALIASES = new Set<string>(["LAB_TEST"]);

/**
 * Whether the opportunity type should show the Service Details section in lead detail UI.
 */
export function opportunityTypeShowsServiceDetails(type: string): boolean {
  if (!type.trim()) return false;
  const canonical = normalizeOpportunityType(type);
  if (canonical && OPPORTUNITY_TYPES_WITH_SERVICE_DETAILS_SET.has(canonical)) {
    return true;
  }
  const raw = normalizeOpportunityTypeAliasKey(type);
  if (SERVICE_DETAILS_EXTRA_TYPE_ALIASES.has(raw)) return true;
  return OPPORTUNITY_TYPES_WITH_SERVICE_DETAILS_SET.has(raw);
}

/** Extra aliases for disposition-config API only (types not in OPPORTUNITY_TYPE_KEYS). */
const DISPOSITION_API_EXTRA_ALIASES: Record<string, string> = {
  LAB_TEST: "LAB",
  DIAGNOSTIC: "DIAGNOSTICS",
  DIAGNOSTICS: "DIAGNOSTICS",
};

/**
 * Normalize opportunity type for GET /admin/disposition-config?opportunityType=.
 * Applies known aliases (e.g. PROCEDURE → DR_CONSULTATION); passes through other types as-is (e.g. REFERRAL).
 */
export function normalizeOpportunityTypeForDispositionApi(
  input: string
): string {
  const normalized = normalizeOpportunityTypeAliasKey(input);
  if (!normalized) return "";
  const canonical = OPPORTUNITY_TYPE_ALIAS_TO_KEY[normalized];
  if (canonical) return canonical;
  return DISPOSITION_API_EXTRA_ALIASES[normalized] ?? normalized;
}

function titleCaseOpportunityTypeKey(input: string): string {
  return input
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * True when apiLabel is a human display string (e.g. "OPD Procedure"), not the
 * raw snake_case key passed twice (e.g. session restore `format(id, id)`).
 */
function isHumanOpportunityTypeApiLabel(apiLabel: string): boolean {
  return /\s/.test(apiLabel) || /[a-z]/.test(apiLabel);
}

/**
 * Format an opportunity type for display.
 *
 * Resolution order:
 *   1. `apiLabel` from GET /opportunities/types when it differs from the raw type key
 *   2. Human `apiLabel` that normalizes to the same token (e.g. "OPD Procedure")
 *   3. Exact match of the key in `OPPORTUNITY_TYPE_LABEL_BY_KEY` (no alias)
 *   4. Alias lookup → canonical built-in label
 *   5. Fallback: title-case the raw key with underscores → spaces
 *
 * When both args are the same raw key (MyLeadsFilters session restore), prefer
 * built-in / title-case — never return `HEALTH_CHECK` / `OPD_PROCEDURE` as UI text.
 *
 * @param input    Raw opportunity type key (e.g. "ADMISSION", "HEALTH_CHECK")
 * @param apiLabel Optional human-readable label from the API. Pass
 *                 `OpportunityTypeItem.label` to honor admin-configured names
 *                 (e.g. "Health Screening" for "HEALTH_CHECK").
 */
export function formatOpportunityTypeLabel(
  input: string,
  apiLabel?: string | null
): string {
  const trimmedApiLabel =
    typeof apiLabel === "string" && apiLabel.trim().length > 0
      ? apiLabel.trim()
      : null;
  const normInput = normalizeOpportunityTypeAliasKey(input);

  if (trimmedApiLabel) {
    const normLabel = normalizeOpportunityTypeAliasKey(trimmedApiLabel);
    if (normInput !== normLabel) {
      return trimmedApiLabel;
    }
    // Same token: keep "OPD Procedure", but not raw "OPD_PROCEDURE"/"HEALTH_CHECK".
    if (isHumanOpportunityTypeApiLabel(trimmedApiLabel)) {
      return trimmedApiLabel;
    }
  }

  if (
    normInput &&
    Object.prototype.hasOwnProperty.call(
      OPPORTUNITY_TYPE_LABEL_BY_KEY,
      normInput
    )
  ) {
    return OPPORTUNITY_TYPE_LABEL_BY_KEY[normInput as OpportunityTypeKey];
  }

  const normalized = normalizeOpportunityType(input);
  if (normalized) return OPPORTUNITY_TYPE_LABEL_BY_KEY[normalized];
  return titleCaseOpportunityTypeKey(input);
}
